import { globalComponents } from 'src/types/globalTypes';
import { useLocation, useNavigate } from 'react-router-dom';
import useRequest from 'src/api/useRequest';
import React, { useCallback, useState } from 'react';
import { patentRightsServiceProvider } from 'src/api/service/patentRightsService';

import DetailPage from '../DetailPage';
import NotFoundPage from 'src/components/layout/notFound/NotFoundPage';
import ResultLoading from 'src/components/ui-kit/ResultLoading';
import DropdownInfo from 'src/components/ui-kit/DropdownInfo';
import { WrapperDetailInfo, NoContent } from 'src/components/contents/detail/DetailPage';
import { changeNumToApplClass } from '../../search/result/PatentResult';
import { IResPatentRightsDetail } from 'src/types/api/patentRightsApiTypes';

import PatentRightBase from './infoComponents/InfoPatentRightBase';
import InfoPatentAnnuity from './infoComponents/InfoPatentAnnuity';
import InfoPatentRights from './infoComponents/InfoPatentRights';
import InfoPatentPriority from './infoComponents/InfoPatentPriority';
import InfoPatentDivide from './infoComponents/InfoPatentDivide';
import InfoPatentChange from './infoComponents/InfoPatentChange';
import InfoPatentTwinsApprove from './infoComponents/InfoPatentTwinsApprove';
import InfoPatentTwinsAnnounce from './infoComponents/InfoPatentTwinsAnnounce';
import InfoPatentAlteration from './infoComponents/InfoPatentAlteration';
import InfoQrCode from '../InfoQrCode';
import QueryString from 'qs';
import { IReqQRCode, QrCodeUuidCheck } from 'src/types/api/qrCodeTypes';
import { useModalStore } from 'src/hooks/useStore';
import UuidAlertModal from '../UuidAlertModal';

// 自訂 react router state 傳遞過來的參數格式
interface CustomizedState {
  applNo: string;
  applyClass: string;
}

const patentRightsService = patentRightsServiceProvider();

/**
 * 專利案件明細
 */
const PatentDetail: React.FC = () => {
  const navigate = useNavigate();
  const setActiveModal = useModalStore((state) => state.setActiveModal);
  const oldUser = useModalStore((state) => state.isOldUser);

  // 表格資訊
  const component = globalComponents.P;
  const colorType = component.color;
  const angleColor = colorType.fifth;
  const NO_CONTENT = <NoContent>(無)</NoContent>; //該欄位無資料顯示項目

  // 控制各區塊收合
  const [isShowPatentRightBase, setIsShowPatentRightBase] = useState<boolean>(true);
  const [isShowPatentAnnuity, setIsShowPatentAnnuity] = useState<boolean>(true);
  const [isShowPatentRights, setIsShowPatentRights] = useState<boolean>(false);
  const [isShowPatentPriority, setIsShowPatentPriority] = useState<boolean>(false);
  const [isShowPatentDivide, setIsShowPatentDivide] = useState<boolean>(false);
  const [isShowPatentChange, setIsShowPatentChange] = useState<boolean>(false);
  const [isShowPatentTwins, setIsShowPatentTwins] = useState<boolean>(false);
  const [isShowPatentAlteration, setIsShowPatentAlteration] = useState<boolean>(false);
  const [isShowQRcode, setIsShowQRcode] = useState<boolean>(false);

  // 取得案件資料
  const location = useLocation();
  let state = null;
  let applNo = '';
  let applyClass = '';

  // QR code資料
  const { search } = useLocation();
  const params = QueryString.parse(search, { ignoreQueryPrefix: true });
  const [qrCode] = useState<IReqQRCode>({
    qrCode: params.QRcode?.toString() || params.QRCode?.toString() || params.QRCODE?.toString(),
    dataNo: params.DataNo?.toString(),
    dataClass: params.DataCls?.toString(),
    uuid: params.UUID?.toString(),
  });

  if (location.state !== null) {
    state = location.state as CustomizedState;
    applNo = state.applNo;
    applyClass = state.applyClass;
  }
  const [isLoading, , res] = useRequest<IResPatentRightsDetail>(
    useCallback(() => {
      return patentRightsService.getPatentRightsByApplNo(applNo, applyClass, qrCode);
    }, [applNo, applyClass, qrCode]),
  );

  const desktopForm = res ? (
    <WrapperDetailInfo>
      <DropdownInfo
        title={`專利類別：${changeNumToApplClass(
          res.patentRights.applicationReference.applyClass,
        )}`}
        color={angleColor}
      />
      <PatentRightBase
        data={res.patentRights}
        certPdfChkDetail={res.certPdfChkDetail}
        isShow={isShowPatentRightBase}
        setIsShow={setIsShowPatentRightBase}
        colorType={colorType}
        noContent={NO_CONTENT}
      />
      {res.patentAnnuity && (
        <InfoPatentAnnuity
          data={res.patentAnnuity}
          isShow={isShowPatentAnnuity}
          setIsShow={setIsShowPatentAnnuity}
          colorType={colorType}
          noContent={NO_CONTENT}
        />
      )}
      <InfoPatentRights
        data={res.patentRights}
        isShow={isShowPatentRights}
        setIsShow={setIsShowPatentRights}
        colorType={colorType}
        noContent={NO_CONTENT}
      />
      {res.patentPriority && (
        <InfoPatentPriority
          data={res.patentPriority}
          isShow={isShowPatentPriority}
          setIsShow={setIsShowPatentPriority}
          colorType={colorType}
          noContent={NO_CONTENT}
        />
      )}
      {res.patentDivide && (
        <InfoPatentDivide
          data={res.patentDivide}
          isShow={isShowPatentDivide}
          setIsShow={setIsShowPatentDivide}
          colorType={colorType}
          noContent={NO_CONTENT}
        />
      )}
      {res.patentChange && (
        <InfoPatentChange
          data={res.patentChange}
          isShow={isShowPatentChange}
          setIsShow={setIsShowPatentChange}
          colorType={colorType}
          noContent={NO_CONTENT}
        />
      )}
      {res.twinsApprove && applyClass === '1' && (
        <InfoPatentTwinsApprove
          data={res.twinsApprove}
          isShow={isShowPatentTwins}
          setIsShow={setIsShowPatentTwins}
          colorType={colorType}
          noContent={NO_CONTENT}
        />
      )}
      {res.twinsAnnounce && applyClass === '2' && (
        <InfoPatentTwinsAnnounce
          data={res.twinsAnnounce}
          isShow={isShowPatentTwins}
          setIsShow={setIsShowPatentTwins}
          colorType={colorType}
          noContent={NO_CONTENT}
        />
      )}
      {res.patentAlteration && (
        <InfoPatentAlteration
          data={res.patentAlteration}
          isShow={isShowPatentAlteration}
          setIsShow={setIsShowPatentAlteration}
          colorType={colorType}
          noContent={NO_CONTENT}
        />
      )}
      <InfoQrCode
        data={res.qrCodeImage}
        isShow={isShowQRcode}
        setIsShow={setIsShowQRcode}
        colorType={colorType}
        noContent={NO_CONTENT}
      />
    </WrapperDetailInfo>
  ) : (
    <NotFoundPage />
  );

  return (
    <>
      <DetailPage
        title={component.title}
        bannerBgImg={component.bgImgResultBanner}
        image={component.hoverImage}
        imgDot={component.bgImgDot}
        bgColor={component.color}
        onClikcReturnPage={() => navigate(-1)}
      >
        {isLoading ? <ResultLoading color={colorType.third} /> : desktopForm}
      </DetailPage>
      {!isLoading &&
        res?.qrCodeApiResultCode === QrCodeUuidCheck.U03 &&
        !oldUser &&
        setActiveModal(<UuidAlertModal />)}
    </>
  );
};

export default PatentDetail;
